import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AlertController,
  LoadingController,
  LoadingOptions,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { HeaderModel } from 'src/app/_component/header/header.model';
import { customValidators } from 'src/app/_helpers/customValidators';
import { globalHelper } from 'src/app/_helpers/global';
import { HeaderService } from 'src/app/_services/header.service';
import { ThisPlatform } from 'src/app/_services/platform.service';
import { CommunityService } from '../../community/community.service';
import { Item, ItemRental, ItemType, RentalStatus } from '../rental.model';
import { RentalService } from '../rental.service';
import { UserService } from './../../../_services/user.service';
import { cl } from '@fullcalendar/core/internal-common';
import { Subject, takeUntil } from 'rxjs';
@Component({
  selector: 'app-rental-details',
  templateUrl: './rental-details.page.html',
  styleUrls: ['./rental-details.page.scss'],
})
export class RentalDetailsPage implements OnInit, OnDestroy {
  form: FormGroup;
  disableSaveButton: boolean = false;
  validationError: boolean = false;
  fileData: File = null;
  deleteAttachments = false;
  itemId: number;
  item: Item = new Item();
  itemRentals: ItemRental[] = [];
  filteredItemRentals: ItemRental[] = [];
  rentalStatus = RentalStatus;
  canEdit: boolean = false;
  canEditStatus: boolean = false;
  canAdd: boolean;
  width: number = window.innerWidth;
  showPageLoader: boolean = true;
  rentalStatusArray = [
    {
      id: 'ALL',
      name: this.translateServ.instant('ALL'),
    },
    {
      id: RentalStatus.Reservation,
      name: this.translateServ.instant('RENTAL.STATUS.RESERVATION'),
    },
    {
      id: RentalStatus.Rent,
      name: this.translateServ.instant('RENTAL.STATUS.RENT'),
    },
    {
      id: RentalStatus.Return,
      name: this.translateServ.instant('RENTAL.STATUS.RETURN'),
    },
  ];
  currentStatusToFilter: string = '';
  isError: boolean = false;
  errorStatus: number;

  loaderConfig: LoadingOptions = {
    keyboardClose: true,
    message: undefined,
    spinner: null,
  };

  headerInit: HeaderModel = {
    title: '',
    backDefaultHref: '/app/rental',
    showRightButtonsOnlyOnMobile: true,
  };

  destroy$: Subject<boolean> = new Subject<boolean>();

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.width = window.innerWidth;
  }

  constructor(
    public rentalServ: RentalService,
    public p: ThisPlatform,
    private el: ElementRef,
    public communityServ: CommunityService,
    private route: ActivatedRoute,
    private translateServ: TranslateService,
    private router: Router,
    public alertController: AlertController,
    private loadingCtrl: LoadingController,
    public userServ: UserService,
    private headerServ: HeaderService,
  ) {}

  async ngOnInit() {
    this.canEdit = await this.userServ.hasRolesAsync(['rental.edit']);
    this.canEditStatus = await this.userServ.hasRolesAsync([
      'rental.change.status',
    ]);

    if (this.canEdit) this.initForm();

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap) => {
      if (paramMap.has('itemId')) {
        this.headerInit.rightButtons = [
          {
            iconName: 'trash',
            rolePermission: ['rental.edit'],
            onClick: () => this.deleteItem(),
          },
        ];

        this.itemId = Number(paramMap.get('itemId'));

        if (this.itemId) {
          this.rentalServ
            .getItem(this.itemId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res: Item) => {
                this.item = res;
                if (this.canEdit) this.loadItemDetails();

                this.headerInit.title = this.canEdit
                  ? this.translateServ.instant('EDIT')
                  : this.item.name;
              },
              error: (err) => {
                this.isError = true;
                this.errorStatus = err.status;
              },
            });
          this.refreshItemRentals();
        } else {
          this.noItemIdOrZero();
        }
      } else {
        this.noItemIdOrZero();
      }
    });
  }

  ionViewWillEnter() {
    this.headerServ.setHeaderInit(this.headerInit);
  }
  ionViewDidEnter() {
    this.showPageLoader = false;
  }

  initForm() {
    this.form = new FormGroup({
      name: new FormControl('', {
        updateOn: 'blur',
        validators: [
          Validators.required,
          Validators.maxLength(50),
          customValidators.trimValidator(),
        ],
      }),
      description: new FormControl('', {
        updateOn: 'blur',
        validators: [Validators.maxLength(1000)],
      }),
      itemImage: new FormControl('', {
        updateOn: 'blur',
        validators: [
          customValidators.fileMaxSize(10000000),
          customValidators.fileFormat([
            'jpg',
            'png',
            'gif',
            'jfif',
            'pjpeg',
            'jpeg',
            'pjp',
          ]),
        ],
      }),
    });
  }

  loadItemDetails() {
    this.form.patchValue({
      name: this.item.name,
      description: this.item.description,
      //itemImage: this.item.image,
    });
  }

  imageUploaded(file: File) {
    this.deleteAttachments = false;
    this.fileData = file;
  }

  imageRemoved($event) {
    this.deleteAttachments = true;
    this.fileData = null;
  }

  onClickSave() {
    if (!this.form.valid) {
      this.validationError = true;
      globalHelper.scrollToFirstInvalidControl(this.el);
      return;
    }
    this.saveItem(this.form.value);
  }

  saveItem(form) {
    this.disableSaveButton = true;
    const formData = new FormData();

    formData.append('name', form.name ? form.name : '');
    formData.append('description', form.description ? form.description : '');
    this.fileData ? formData.append('file', this.fileData) : '';
    if (this.deleteAttachments) {
      formData.append('deleteAttachments', JSON.stringify(true));
    }

    if (this.itemId) {
      this.rentalServ
        .putItem(this.itemId, formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          this.disableSaveButton = false;
          this.router.navigateByUrl('/app/rental');
        });
    } else {
      formData.append('type', JSON.stringify(ItemType.Book));

      this.rentalServ
        .postItem(formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          this.disableSaveButton = false;
          this.router.navigateByUrl('/app/rental');
        });
    }
  }

  async deleteItem() {
    let confirm = await globalHelper.Alert(
      this.alertController,
      this.translateServ,
    );
    if (confirm) {
      const conf = this.getDeletedLoadingConfig();

      this.loadingCtrl.create(conf).then((loadingEl) => {
        loadingEl.present();
        this.rentalServ
          .deleteItem(this.itemId)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            loadingEl.dismiss();
            this.router.navigateByUrl('/app/rental');
          });
      });
    }
  }

  refreshItemRentals() {
    this.rentalServ
      .getItemRentals(this.itemId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.itemRentals = res;
        this.setFilteredItemRentals();
      });
  }

  async onClickChangeStatus(itemRentalId: number, newStatus: RentalStatus) {
    let confirm = await globalHelper.Alert(
      this.alertController,
      this.translateServ,
      this.translateServ.instant('RENTAL.DO_YOU_STATUS_CHANGED') +
        this.translateServ.instant('RENTAL.STATUS.' + newStatus) +
        '?',
    );
    if (confirm) {
      const conf = this.setLoaderConfig();
      this.loadingCtrl.create(conf).then((loadingEl) => {
        loadingEl.present();
        let itemRental = new ItemRental();
        itemRental.status = newStatus;
        this.rentalServ
          .putItemRental(itemRentalId, itemRental)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.refreshItemRentals();
              loadingEl.dismiss();
              this.rentalServ.showAlertAfterRequestHTTP(
                'RENTAL.STATUS_CHANGED',
              );
            },
            error: () => {
              loadingEl.dismiss();
              this.rentalServ.showAlertAfterRequestHTTP(null, true);
            },
          });
      });
    }
  }

  async onClickReservation() {
    await this.rentalServ.itemRentalReservation(this.itemId);
    this.refreshItemRentals();
  }

  async onClickDeleteItemRental(itemRentalId: number) {
    let confirm = await globalHelper.Alert(
      this.alertController,
      this.translateServ,
    );
    if (confirm) {
      const conf = this.getDeletedLoadingConfig();
      this.loadingCtrl.create(conf).then((loadingEl) => {
        loadingEl.present();
        this.rentalServ
          .deleteItemRental(itemRentalId)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.refreshItemRentals();
            loadingEl.dismiss();
          });
      });
    }
  }

  setFilteredItemRentals() {
    if (this.currentStatusToFilter === '') {
      this.filteredItemRentals = this.itemRentals;
    } else {
      this.filteredItemRentals = this.itemRentals.filter(
        (e) => e.status === this.currentStatusToFilter,
      );
    }
  }
  onDropdownValueChanged(event) {
    if (event.length === 0 || event.id === 'ALL') {
      this.currentStatusToFilter = '';
    } else {
      this.currentStatusToFilter = event.id;
    }
    this.setFilteredItemRentals();
  }

  noItemIdOrZero() {
    if (this.canEdit) {
      this.headerInit.title = this.translateServ.instant('NEW');
    } else {
      this.router.navigateByUrl('/app/rental');
    }
  }

  onClickNotify(id: number) {
    const conf = this.setLoaderConfig();
    this.loadingCtrl.create(conf).then((loadingEl) => {
      loadingEl.present();
      this.rentalServ
        .notyfiItemRental(id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.rentalServ.showAlertAfterRequestHTTP('RENTAL.NOTIFIED');
          },
          error: () => {
            this.rentalServ.showAlertAfterRequestHTTP(null, true);
          },
        });
      loadingEl.dismiss();
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  /** Set up loader config */
  private setLoaderConfig(): LoadingOptions {
    const conf = { ...this.loaderConfig };
    conf.spinner = 'dots';

    return conf;
  }

  private getDeletedLoadingConfig(): LoadingOptions {
    const conf = {
      ...this.loaderConfig,
      message: `<img src="./assets/loading.svg"><br><span>${this.translateServ.instant('DELETING_NO_DOTS')}</span>`,
      dots: null,
    };
    return conf;
  }
}
